import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "dark" | "light";

interface ThemeState {
  theme: Theme | null;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const applyThemeToDOM = (theme: Theme) => {
  if (typeof document === "undefined") return;

  if (theme === "dark") {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
};

const getSystemTheme = (): Theme => {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: null,
      setTheme: (newTheme: Theme) => {
        set({ theme: newTheme });
        applyThemeToDOM(newTheme);
      },
      toggleTheme: () => {
        const currentTheme = get().theme || getSystemTheme();
        const newTheme = currentTheme === "dark" ? "light" : "dark";
        set({ theme: newTheme });
        applyThemeToDOM(newTheme);
      },
    }),
    {
      name: "theme-storage",
      partialize: (state) => ({ theme: state.theme }),
    },
  ),
);

if (typeof window !== "undefined") {
  // Initialize with system theme if no stored preference
  const systemTheme = getSystemTheme();
  const storedTheme = useThemeStore.getState().theme;
  const initialTheme = storedTheme || systemTheme;
  useThemeStore.getState().setTheme(initialTheme);

  try {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    // Store original system theme for comparison
    let lastSystemTheme = systemTheme;

    const fullUpdateHandler = (event: MediaQueryListEvent) => {
      const newSystemTheme = event.matches ? "dark" : "light";
      // Only follow system changes if user hasn't explicitly set a preference
      const storedState = useThemeStore.getState();
      // Always update if user hasn't changed the theme since last system update
      if (storedState.theme === lastSystemTheme) {
        useThemeStore.getState().setTheme(newSystemTheme);
      }
      lastSystemTheme = newSystemTheme;
    };

    mediaQuery.addEventListener("change", fullUpdateHandler);
    window.addEventListener("beforeunload", () => {
      mediaQuery.removeEventListener("change", fullUpdateHandler);
    });
  } catch (error) {
    console.warn("Could not set up system theme listener:", error);
  }
}
